import { useRef, useState } from 'react'

import { Check, Columns, Eye, EyeOff } from 'lucide-react'

import { Button } from '@/components/common/Button/Button'
import { DropdownPortal } from '@/components/common/Dropdown/DropdownPortal'
import { cn } from '@/utils/cn'

import type { ColumnVisibilityToggleProps } from '../DataTable.types'

export const ColumnVisibilityToggle = <TData,>({
  table,
  className,
  showLabel = true
}: ColumnVisibilityToggleProps<TData>) => {
  const [isOpen, setIsOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)

  const allColumns = table.getAllColumns()
  const hiddenCount = allColumns.filter((column) => !column.getIsVisible()).length
  const canHideColumns = allColumns.filter((column) => column.getCanHide()).length

  const toggleAll = () => {
    const allVisible = allColumns.every((column) => column.getIsVisible() || !column.getCanHide())

    if (allVisible) {
      // Hide all hideable columns except the first one (usually selection or primary)
      allColumns.forEach((column, index) => {
        if (column.getCanHide() && index > 0) {
          column.toggleVisibility(false)
        }
      })
    } else {
      // Show all columns
      allColumns.forEach((column) => {
        if (column.getCanHide()) {
          column.toggleVisibility(true)
        }
      })
    }
  }

  const resetToDefaults = () => {
    allColumns.forEach((column) => {
      if (column.getCanHide()) {
        const defaultHidden = (column.columnDef as any).meta?.defaultHidden || false
        column.toggleVisibility(!defaultHidden)
      }
    })
  }

  if (canHideColumns === 0) {
    return null // No hideable columns
  }

  return (
    <div className={cn('relative', className)}>
      <Button
        ref={buttonRef}
        variant="secondary"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="relative"
        aria-label="Configurar visibilidad de columnas"
      >
        <Columns className={cn('h-4 w-4', showLabel && 'mr-2')} />
        {showLabel && 'Columnas'}
        {hiddenCount > 0 && (
          <span className="ml-1 px-1.5 py-0.5 text-xs bg-primary text-primary-foreground rounded-full">
            {hiddenCount}
          </span>
        )}
      </Button>

      <DropdownPortal isOpen={isOpen} targetRef={buttonRef} placement="bottom-end" className="w-64">
        <div className="bg-white border border-gray-200 rounded-md shadow-lg">
          <div className="p-3 border-b">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-medium text-sm">Visibilidad de columnas</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsOpen(false)}
                className="h-6 w-6 p-0"
              >
                ×
              </Button>
            </div>

            <div className="flex gap-2">
              <Button variant="secondary" size="sm" onClick={toggleAll} className="flex-1 text-xs">
                {hiddenCount === 0 ? 'Ocultar todas' : 'Mostrar todas'}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={resetToDefaults}
                className="flex-1 text-xs"
              >
                Restablecer
              </Button>
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto">
            {allColumns.map((column) => {
              if (!column.getCanHide()) return null

              const isVisible = column.getIsVisible()
              const columnId = column.id
              const header =
                typeof column.columnDef.header === 'string' ? column.columnDef.header : columnId

              return (
                <div
                  key={columnId}
                  className="flex items-center gap-3 px-3 py-2 hover:bg-muted cursor-pointer"
                  onClick={() => column.toggleVisibility()}
                >
                  <div className="flex items-center justify-center w-4 h-4">
                    {isVisible ? (
                      <Check className="h-3 w-3 text-primary" />
                    ) : (
                      <div className="w-3 h-3" />
                    )}
                  </div>

                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {isVisible ? (
                      <Eye className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                    ) : (
                      <EyeOff className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                    )}
                    <span className="text-sm truncate">{header}</span>
                  </div>
                </div>
              )
            })}
          </div>

          <div className="p-3 border-t bg-muted/30">
            <p className="text-xs text-muted-foreground">
              {allColumns.filter((c) => c.getIsVisible() && c.getCanHide()).length} de{' '}
              {canHideColumns} columnas
            </p>
          </div>
        </div>
      </DropdownPortal>

      {isOpen && (
        <div
          className="fixed inset-0 z-dropdown-backdrop"
          onClick={() => setIsOpen(false)}
          onKeyDown={(e) => e.key === 'Escape' && setIsOpen(false)}
          role="button"
          tabIndex={-1}
          aria-label="Close dropdown"
        />
      )}
    </div>
  )
}

// Compact version for mobile
export const CompactColumnVisibility = <TData,>({
  table,
  className,
  showLabel = true
}: ColumnVisibilityToggleProps<TData>) => {
  const [isOpen, setIsOpen] = useState(false)
  const hiddenCount = table.getAllColumns().filter((column) => !column.getIsVisible()).length

  return (
    <div className={cn('relative', className)}>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="p-2"
        aria-label={showLabel ? 'Columnas' : 'Configurar visibilidad de columnas'}
      >
        <Columns className="h-4 w-4" />
        {hiddenCount > 0 && (
          <span className="absolute -top-1 -right-1 h-4 w-4 text-xs bg-primary text-primary-foreground rounded-full flex items-center justify-center">
            {hiddenCount}
          </span>
        )}
      </Button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-dropdown-backdrop" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-56 bg-background border rounded-md shadow-lg z-dropdown">
            <div className="p-2">
              <h3 className="font-medium text-sm mb-2">{showLabel ? 'Columnas' : 'Visibilidad'}</h3>
              <div className="space-y-1">
                {table.getAllColumns().map((column) => {
                  if (!column.getCanHide()) return null

                  const isVisible = column.getIsVisible()
                  const header =
                    typeof column.columnDef.header === 'string'
                      ? column.columnDef.header
                      : column.id

                  return (
                    <label
                      key={column.id}
                      className="flex items-center gap-2 text-sm cursor-pointer p-1 hover:bg-muted rounded"
                    >
                      <input
                        type="checkbox"
                        checked={isVisible}
                        onChange={() => column.toggleVisibility()}
                        className="rounded border-gray-300"
                      />
                      <span className="truncate">{header}</span>
                    </label>
                  )
                })}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
